import { supabase } from '@/lib/supabase';

export type SupplierPipelineStatusLevel = 'healthy' | 'degraded' | 'stale' | 'unknown';

interface PipelineRunStatus {
  last_run_at?: string | null;
  last_completed_at?: string | null;
  last_status: string;
  last_error?: string | null;
}

export interface SupplierDocumentPipelineStatus {
  generated_at: string;
  status: SupplierPipelineStatusLevel;
  headline: string;
  /**
   * E-tickets awaiting a match or review. A work list, shown as a number; it no longer degrades the status
   * (migration 20261010310000). Optional: absent before that migration is live.
   */
  review_items?: number;
  invoice_ingestion: PipelineRunStatus & {
    latest_document_at?: string | null;
    pending: number;
    parse_errors: number;
    // Documents the sync orchestrator terminally retired (status 'ignored')
    // because they had no stored file to parse. Informational — excluded from the
    // pill's degraded logic, surfaced so ops can see them distinctly from a
    // transient error. Optional: reads 0 until the terminal-classification
    // migration is live.
    terminal_file_missing?: number;
    invalid_ticket_values: number;
  };
  ticket_ingestion: PipelineRunStatus & {
    latest_document_at?: string | null;
    unmatched_or_review: number;
    failed: number;
  };
  /** The sync that links supplier documents to bookings. A partial or failed run degrades the status. */
  supplier_sync?: PipelineRunStatus;
  gds_verification: PipelineRunStatus & {
    /** Current bookings past the 2-day GDS re-check. Since 20261010310000 this excludes the two counts below. */
    overdue_candidates: number;
    /** Overdue by the old rule but departed more than departed_grace_days ago. Number only. */
    excluded_departed?: number;
    /**
     * Overdue by the old rule, the refresh ledger calls the PNR unreadable (purged, refused, invalid) and the departure
     * is known and not in the future. Number only; a future trip is always counted.
     */
    excluded_unreadable?: number;
    /** Unreadable per the ledger with no departure date: left out of the count, shown as its own number. */
    unreadable_no_date?: number;
    /** Rows that reached an unreadable state in the last 24 hours; more than 10 degrades the status (outage guard). */
    newly_unreadable_24h?: number;
    /** The last successful live read; none for 24 hours while reads continue degrades the status (outage guard). */
    last_successful_read_at?: string | null;
    read_outage?: boolean;
    departed_grace_days?: number;
    supports: string[];
  };
  schedules: {
    invoice_minutes: number;
    ticket_minutes: number;
    // null when the canonical live-GDS worker (pnt-deadline-checker) is not
    // scheduled; a real cadence derived from its pg_cron entry when it is.
    gds_minutes: number | null;
    status_refresh_seconds: number;
  };
}

export const supplierDocumentPipelineStatusService = {
  async getStatus(signal?: AbortSignal): Promise<SupplierDocumentPipelineStatus> {
    // The migration lands before the generated database types are refreshed.
    const request = supabase.rpc('supplier_document_pipeline_status_get' as never);
    const { data, error } = await (signal ? request.abortSignal(signal) : request);
    if (error) throw error;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Supplier document pipeline returned an invalid response.');
    }
    return data as SupplierDocumentPipelineStatus;
  },
};
